import React, { useRef, useState } from 'react';
import AppShell from '@/components/AppShell';
import client, { API, getToken } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { StatusBadge } from '@/components/StatusBadge';
import { toast } from 'sonner';
import { Loader2, Camera } from 'lucide-react';

const ROLE_LABELS = { developer: 'Developer (Super Admin)', company_admin: 'Company Admin', der: 'DER', owner_operator: 'Owner Operator', driver: 'Driver' };

export default function Profile() {
  const { user, setUser, checkAuth } = useAuth();
  const [f, setF] = useState({ name: user?.name || '', phone: user?.phone || '', title: user?.title || '' });
  const [saving, setSaving] = useState(false);
  const [avatarKey, setAvatarKey] = useState(Date.now());
  const fileRef = useRef();
  const initials = (user?.name || 'U').split(' ').map((s) => s[0]).slice(0,2).join('').toUpperCase();
  const avatarUrl = user?.picture_path ? `${API}/avatar/${user.user_id}?t=${avatarKey}&auth=${getToken()}` : user?.picture;

  const save = async () => {
    setSaving(true);
    try { const r = await client.put('/auth/profile', f); setUser(r.data); toast.success('Profile updated'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };
  const uploadPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try { await client.post('/auth/profile/photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success('Photo updated'); await checkAuth(); setAvatarKey(Date.now()); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
  };

  return (
    <AppShell title="My Profile">
      <Card className="max-w-lg p-6">
        <div className="mb-6 flex items-center gap-4">
          <div className="relative">
            {avatarUrl ? <img src={avatarUrl} alt="avatar" className="h-20 w-20 rounded-full object-cover" onError={(e) => { e.target.style.display='none'; }} /> :
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[hsl(var(--primary))] text-2xl font-semibold text-white">{initials}</div>}
            <button onClick={() => fileRef.current?.click()} className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-[hsl(var(--gold))] shadow" data-testid="profile-photo-button"><Camera className="h-4 w-4" /></button>
            <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg" className="hidden" onChange={(e) => uploadPhoto(e.target.files[0])} data-testid="profile-photo-input" />
          </div>
          <div><div className="font-display text-xl font-semibold">{user?.name}</div><div className="text-sm text-muted-foreground">{user?.email}</div><div className="mt-1"><StatusBadge status={user?.status === 'active' ? 'active' : 'pending'} /></div></div>
        </div>
        <div className="space-y-3">
          <div><Label>Full Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1" data-testid="profile-name-input" /></div>
          <div><Label>Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="mt-1" data-testid="profile-phone-input" /></div>
          <div><Label>Job Title</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1" /></div>
          <div className="flex justify-between border-t pt-3 text-sm"><span className="text-muted-foreground">Role</span><span className="font-medium">{ROLE_LABELS[user?.role]}</span></div>
          <Button onClick={save} disabled={saving} className="w-full" data-testid="profile-save-button">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save Changes</Button>
        </div>
      </Card>
    </AppShell>
  );
}
